import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import IDPSettingsForm from '../../components/IDPSettings/IDPSettingsForm';
import ErrorAlert from '../../components/widgets/ErrorAlert';
import HeadingWithIcon from '../../components/widgets/HeadingWithIcon';
import LoadingSpinner from '../../components/widgets/LoadingSpinner';
import SuccessAlert from '../../components/widgets/SuccessAlert';
import axiosInstance from '../../network/ApiCall';
import API from '../../network/ApiCall';
import {SYSTEM_UNION_CODE} from "../../config";
import IDPSettingsTable from "../../components/IDPSettings/IDPSettingsTable";

const ROLE_MAPPING_KEYS = ['USER', 'ADMIN', 'UNION_ADMIN'];


const IDPSettings = () => {

  const IDP_SETTINGS_ENDPOINT = "/idpSettings";

  const {t} = useTranslation();
  const authState = useSelector((state) => state.auth);
  const params = useParams();
  const [formState, setFormState] = useState({success: false, error: null, submissionDate: null});
  const [defaultData, setDefaultData] = useState();
  const [loading, setLoading] = useState(true);

  const userCountryCode = authState?.user?.countryCode;
  const scrollToTop = () => {
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      window.scrollTo({top: 0, behavior: 'smooth'});
    }
  };

  const unionModeEnabled = Boolean(SYSTEM_UNION_CODE);
  const isUnionAdmin = authState?.user?.roles?.includes('UNION_ADMIN');
  const routeCountryCode = params?.countryCode;
  const normalizedCountryCode = routeCountryCode
    ? routeCountryCode.toUpperCase()
    : (!unionModeEnabled || !isUnionAdmin ? (userCountryCode ? userCountryCode.toUpperCase() : '') : '');
  const shouldShowTable = unionModeEnabled && isUnionAdmin && !routeCountryCode;

  const toFormValues = useCallback((dto = {}) => ({
    countryCode: dto.countryCode ?? normalizedCountryCode ?? '',
    enabled: dto.enabled ?? false,
    idpType: dto.idpType ?? 'OIDC_SECRET',
    oauth2IssuerUri: dto.oauth2IssuerUri ?? '',
    oauth2ClientId: dto.oauth2ClientId ?? '',
    oauth2ClientSecret: dto.oauth2ClientSecret ?? '',
    oauth2ScopesCsv: dto.oauth2ScopesCsv ?? '',
    samlMetadataUrl: dto.samlMetadataUrl ?? '',
    samlMetadataXml: dto.samlMetadataXml ?? '',
    samlEntityId: dto.samlEntityId ?? '',
    samlNameIdFormat: dto.samlNameIdFormat ?? '',
    samlWantAssertionsSigned: dto.samlWantAssertionsSigned ?? true,
    samlSignAuthnRequests: dto.samlSignAuthnRequests ?? false,
    roleMappings: ROLE_MAPPING_KEYS.reduce((accumulator, role) => {
      const value = dto?.roleMappings?.[role];
      accumulator[role] = value ?? '';
      return accumulator;
    }, {}),
  }), [normalizedCountryCode]);

  const toPayload = (formValues) => {
    const normalizeString = (value) => {
      if (value === undefined || value === null) {
        return null;
      }
      const trimmed = typeof value === 'string' ? value.trim() : value;
      return trimmed === '' ? null : trimmed;
    };

    const normalizeBoolean = (value, fallback = false) => {
      if (typeof value === 'boolean') {
        return value;
      }
      if (value === 'on') {
        return true;
      }
      return fallback;
    };

    return {
      countryCode: normalizedCountryCode,
      enabled: normalizeBoolean(formValues.enabled),
      idpType: normalizeString(formValues.idpType)?.toUpperCase() ?? null,
      oauth2IssuerUri: normalizeString(formValues.oauth2IssuerUri),
      oauth2ClientId: normalizeString(formValues.oauth2ClientId),
      oauth2ClientSecret: normalizeString(formValues.oauth2ClientSecret),
      oauth2ScopesCsv: normalizeString(formValues.oauth2ScopesCsv),
      samlMetadataUrl: normalizeString(formValues.samlMetadataUrl),
      samlMetadataXml: normalizeString(formValues.samlMetadataXml),
      samlEntityId: normalizeString(formValues.samlEntityId),
      samlNameIdFormat: normalizeString(formValues.samlNameIdFormat),
      samlWantAssertionsSigned: normalizeBoolean(formValues.samlWantAssertionsSigned, true),
      samlSignAuthnRequests: normalizeBoolean(formValues.samlSignAuthnRequests, false),
      roleMappings: ROLE_MAPPING_KEYS.reduce((accumulator, role) => {
        const normalized = normalizeString(formValues?.roleMappings?.[role]);
        if (normalized !== null) {
          accumulator[role] = normalized;
        }
        return accumulator;
      }, {}),
    };
  };

  useEffect(() => {
    let ignore = false;

    const loadSettings = async () => {
      if (shouldShowTable) {
        setLoading(false);
        setDefaultData(undefined);
        setFormState({success: false, error: null, submissionDate: null});
        return;
      }
      if (!normalizedCountryCode) {
        if (authState.loading || authState.loadingUser) {
          return;
        }
        setLoading(false);
        setDefaultData(undefined);
        setFormState({success: false, error: t('idpSettings:msgMissingCountry'), submissionDate: new Date()});
        return;
      }

      setLoading(true);
      try {
        const response = await API.get(`${IDP_SETTINGS_ENDPOINT}/${normalizedCountryCode}`);
        if (ignore) {
          return;
        }
        setDefaultData(toFormValues(response.data || {}));
        setFormState({success: false, error: null, submissionDate: null});
      } catch (error) {
        if (ignore) {
          return;
        }
        if (error?.response?.status === 404) {
          setDefaultData(toFormValues({}));
          setFormState({success: false, error: null, submissionDate: null});
        } else {
          console.error('ERROR LOADING IDP SETTINGS: ', error);
          setDefaultData(undefined);
          setFormState({success: false, error: t('common:msgUnknownApiError'), submissionDate: new Date()});
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    loadSettings();

    return () => {
      ignore = true;
    };
  }, [authState.loading, authState.loadingUser, normalizedCountryCode, shouldShowTable, t, toFormValues]);

  const handleSubmit = (data) => {
    if (!normalizedCountryCode) {
      setFormState({success: false, error: t('idpSettings:msgMissingCountry'), submissionDate: new Date()});
      return;
    }

    const payload = toPayload(data);

    axiosInstance.post(`${IDP_SETTINGS_ENDPOINT}/${normalizedCountryCode}`, payload)
      .then((response) => {
        const responseData = response?.data ? toFormValues(response.data) : toFormValues(payload);
        setDefaultData(responseData);
        setFormState({success: true, error: null, submissionDate: new Date()});
        scrollToTop();
      })
      .catch((error) => {
        console.error('ERROR SAVING IDP SETTINGS: ', error);
        setFormState({success: false, error: t('common:msgUnknownApiError'), submissionDate: new Date()});
        scrollToTop();
      });
  };

  const handleError = (errors) => {
    console.warn('FORM VALIDATION FAILED', errors);
  };

  const countryLabel = normalizedCountryCode ? t(`countries:${normalizedCountryCode}`) : '';

  return (
      <div className="content p-4">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><Link to="/">{t('dashboard:hdgHeading')}</Link></li>
            <li className="breadcrumb-item active" aria-current="page">{t('idpSettings:hdgHeading')}</li>
          </ol>
        </nav>
        <div className="m-2 m-sm-5">
          <div className="card my-5">
            <div className="card-header ">
              <div className="row">
                <div className="col">
                  <HeadingWithIcon icon="fa-user-shield">{t('idpSettings:hdgHeading')}</HeadingWithIcon>
                </div>
              </div>
            </div>
            <div className="card-body ">

              {shouldShowTable ? (
                  <IDPSettingsTable/>
                ) :
                (
                  <div className="row ">
                    <div className="col-md-10 offset-md-1 mb-5">
                      <SuccessAlert message={formState.success ? t('common:msgChangesSaved') : null}
                                    lastStateUpdate={formState.submissionDate}/>
                      <ErrorAlert error={formState.error} lastStateUpdate={formState.submissionDate}/>

                      <>

                        <div className="mb-4">
                          <strong>{t('idpSettings:lblCountry')}:</strong> {normalizedCountryCode ? `${normalizedCountryCode} - ${countryLabel}` : t('idpSettings:msgMissingCountry')}
                        </div>

                        {loading ? (
                          <LoadingSpinner/>
                        ) : (
                          <>
                            {normalizedCountryCode && defaultData && (
                              <IDPSettingsForm
                                defaultValues={defaultData}
                                onSubmit={handleSubmit}
                                onError={handleError}
                              />
                            )}
                          </>
                        )}
                      </>

                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
  );
};

export default IDPSettings;
